import { Duration } from "luxon"

import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchBookingsSummary,
    fetchBookingSummaryRatePerResource,
    ServiceCenterBookingSummaryReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { BookingSummaryEntry, BookingSummaryOverview } from "../../statisticsTypes"
import { ResourceTypeFanoutData, useResourceTypeFanout } from "../resourceDashboard/useResourceTypeFanout"

const selectBookingSummaries = (data: ServiceCenterBookingSummaryReportResponse): BookingSummaryEntry[] =>
    data?.bookingSummaries ?? []

type BookingDashboardParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
}>

export type BookingDashboardData = ResourceTypeFanoutData<BookingSummaryEntry> &
    Readonly<{
        summary: BookingSummaryOverview | undefined
    }>

export function useBookingDashboardData({
    serviceCenterId,
    startDate,
    endDate,
}: BookingDashboardParams): BookingDashboardData {
    const enabled = serviceCenterId > 0

    const fanout = useResourceTypeFanout({
        fetch: fetchBookingSummaryRatePerResource,
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY_PER_RESOURCE,
        select: selectBookingSummaries,
        serviceCenterId,
        startDate,
        endDate,
        enabled,
    })

    const summaryResult = useApiQuery(fetchBookingsSummary, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY,
        dependencies: { serviceCenterId, startDate, endDate },
        errorDisplay: ErrorDisplay.NONE,
        enabled,
    })

    return { ...fanout, summary: summaryResult.data?.reportSummary }
}

// --- Client-side fallbacks (Luxon-only) used when the aggregate endpoint is unavailable ---

export function sumNumberOfBookings(entries: ReadonlyArray<BookingSummaryEntry>): number {
    return entries.reduce((sum, entry) => sum + entry.numberOfBookings, 0)
}

/**
 * Booking-weighted mean of the per-resource average durations (a plain mean of averages would
 * over-weight rarely-booked resources). Returns a `Duration` shifted to hours/minutes for display.
 */
export function weightedAverageDuration(entries: ReadonlyArray<BookingSummaryEntry>): Duration {
    let totalBookings = 0
    let weightedMillis = 0
    for (const entry of entries) {
        totalBookings += entry.numberOfBookings
        weightedMillis += Duration.fromISO(entry.averageDuration).toMillis() * entry.numberOfBookings
    }

    const averageMinutes =
        totalBookings > 0 ? Math.round(Duration.fromMillis(weightedMillis / totalBookings).as("minutes")) : 0

    return Duration.fromObject({ minutes: averageMinutes }).shiftTo("hours", "minutes")
}
